import React, { useState, useEffect } from "react";
import { FolderIcon } from "@heroicons/react/24/outline";
import ProjectCard from "../ProjectCard/ProjectCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const bustCache = (url) => `${url}?v=${Date.now()}`;

const ProjectSection = ({ user, userEmail, isAdmin, username, preferences = {} }) => {
  const [ownedProjects, setOwnedProjects] = useState([]);
  const [memberProjects, setMemberProjects] = useState([]);
  const [activeTab, setActiveTab] = useState("creations");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        if (!userEmail) return;
        const token = localStorage.getItem("token");
        if (!token) return;

        const ownedRes = await fetch(`${BACKEND_URL}/projects/user/${userEmail}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ownedData = await ownedRes.json();
        if (ownedRes.ok) {
          setOwnedProjects((ownedData.projects || []).map(p => ({ ...p, image: bustCache(p.image) })));
        }

        const memberRes = await fetch(`${BACKEND_URL}/projects/user/${userEmail}/member`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const memberData = await memberRes.json();
        if (memberRes.ok) {
          setMemberProjects((memberData.projects || []).map(p => ({ ...p, image: bustCache(p.image) })));
        }
      } catch (error) {
        console.error("❌ Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [userEmail]);

  const navBtnClass = (tab) =>
    `px-4 py-2 rounded-full text-sm font-medium transition-all ${
      activeTab === tab
        ? "bg-blue-600 text-white shadow-sm"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`;

  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center text-center p-10 rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500">
      <FolderIcon className="h-10 w-10 mb-2 text-gray-400" />
      <p className="italic">{message}</p>
    </div>
  );

  return (
    <div className="mt-12">
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Projects</h3>
          <div className="flex gap-2">
            <button onClick={() => setActiveTab("creations")} className={navBtnClass("creations")}>
              Creations
            </button>
            <button onClick={() => setActiveTab("contributions")} className={navBtnClass("contributions")}>
              Contributions
            </button>
          </div>
        </div>

        {/* Loader */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(3).fill().map((_, i) => (
              <div key={i} className="h-40 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        )}

        {/* Creations */}
        {!loading && activeTab === "creations" && (
          ownedProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ownedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  isAdmin={isAdmin}
                  username={username}
                />
              ))}
            </div>
          ) : (
            <EmptyState message="No creations found." />
          )
        )}

        {/* Contributions */}
        {!loading && activeTab === "contributions" && (
          (!preferences?.hideContributions || isAdmin) ? (
            memberProjects.length > 0 ? (
              <>
                {preferences?.hideContributions && isAdmin && (
                  <p className="text-center text-sm text-gray-400 mb-4">🔒 Hidden from public</p>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {memberProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      isAdmin={isAdmin}
                      username={username}
                    />
                  ))}
                </div>
              </>
            ) : (
              <EmptyState message="No contributions found." />
            )
          ) : (
            <EmptyState message="🔒 Contributions are hidden by the user." />
          )
        )}
      </div>
    </div>
  );
};

export default ProjectSection;
